import { Search } from '@shared/components/ui/Search';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SEARCH_DEBOUNCE_MS = 200;

interface FiltersSectionProps {
  initialValue?: string;
  onSearchChange: (value: string) => void;
}

export const FiltersSection = ({ initialValue = '', onSearchChange }: FiltersSectionProps) => {
  const { t } = useTranslation('rolesModule');
  const [inputValue, setInputValue] = useState(initialValue);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchChangeRef = useRef(onSearchChange);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  const emitSearch = useCallback((value: string) => {
    onSearchChangeRef.current(value.trim());
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        emitSearch(newValue);
      }, SEARCH_DEBOUNCE_MS);
    },
    [emitSearch],
  );

  const handleSearchSubmit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    emitSearch(inputValue);
  }, [emitSearch, inputValue]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Search
      placeholder={t('search.placeholder')}
      value={inputValue}
      onChange={handleInputChange}
      onEnter={handleSearchSubmit}
      dataTestId="search"
      inputDataTestId="search-input"
      wrapperClassName="w-full sm:max-w-xs"
    />
  );
};
